import { useQuery } from '@tanstack/react-query';
import { useApiClient } from 'src/hooks/useApiClient';
import type * as MT from 'src/motornet/types';
import type { VehicleRef, Make, Model, Trim } from './domain';
import {
	normalize,
	normalizeMake,
	normalizeModel,
	normalizeTrim,
	toStockVehicle,
} from './normalize';

export const MN_MAKES_QUERY_KEY = 'mn_configurable_makes';
export const MN_MODELS_QUERY_KEY = 'mn_configurable_models';
export const MN_TRIMS_QUERY_KEY = 'mn_configurable_trims';
export const MN_STOCK_QUERY_KEY = 'mn_configurator_stock';

export type StockListResult = {
	vehicles: MT.StockVehicle[];
	total: number;
};

export const selectStockList = (
	res: MT.ConfigurableStockResponse
): StockListResult => ({
	vehicles: res.items
		.map(toStockVehicle)
		.sort(
			(a, b) =>
				a.price.totalNoVat - b.price.totalNoVat || a.id.localeCompare(b.id)
		),
	total: res.total,
});

export const useMakes = <TData = Make[]>(select?: (data: Make[]) => TData) => {
	const { apiClient } = useApiClient();

	return useQuery({
		queryKey: [MN_MAKES_QUERY_KEY],
		queryFn: async () => {
			const list =
				await apiClient.motornetControllerService.getPlatformConfigurableMakes();
			return list.map(normalizeMake);
		},
		staleTime: 10 * 60_000,
		gcTime: 15 * 60_000,
		retry: false,
		refetchOnWindowFocus: false,
		select,
	});
};

export const useModels = <TData = Model[]>(
	makeCode: string | null,
	select?: (data: Model[]) => TData
) => {
	const { apiClient } = useApiClient();

	return useQuery({
		queryKey: [MN_MODELS_QUERY_KEY, makeCode],
		queryFn: async () => {
			const list =
				await apiClient.motornetControllerService.getPlatformConfigurableModels(
					makeCode!
				);
			return list.map(normalizeModel);
		},
		enabled: !!makeCode,
		staleTime: 10 * 60_000,
		gcTime: 15 * 60_000,
		retry: false,
		refetchOnWindowFocus: false,
		select,
	});
};

export const MN_VEHICLE_QUERY_KEY = 'mn_configurator_vehicle';

type ApiClientLike = ReturnType<typeof useApiClient>['apiClient'];

export const trimsQueryOptions = (
	apiClient: ApiClientLike,
	makeCode: string,
	modelCode: string
) => ({
	queryKey: [MN_TRIMS_QUERY_KEY, makeCode, modelCode],
	queryFn: async (): Promise<Trim[]> => {
		const res =
			await apiClient.motornetControllerService.getPlatformConfigurableList({
				make: makeCode,
				model: modelCode,
				excluded: false,
			});
		return res.items.map((item) => normalizeTrim(item));
	},
	staleTime: 5 * 60_000,
	gcTime: 10 * 60_000,
	retry: false,
});

export const vehicleQueryOptions = (
	apiClient: ApiClientLike,
	ref: VehicleRef
) => ({
	queryKey: [
		MN_VEHICLE_QUERY_KEY,
		ref.eurotaxCode,
		ref.vehicleKind,
		ref.year,
		ref.month,
	],
	queryFn: async () => {
		const payload =
			await apiClient.motornetControllerService.getMotornetVehicle(
				ref.eurotaxCode,
				ref.vehicleKind,
				{ year: ref.year, month: ref.month }
			);
		return normalize(payload);
	},
	staleTime: 5 * 60_000,
	gcTime: 10 * 60_000,
	retry: false,
});

export const useTrims = <TData = Trim[]>(
	makeCode: string | null,
	modelCode: string | null,
	select?: (data: Trim[]) => TData
) => {
	const { apiClient } = useApiClient();

	return useQuery({
		...trimsQueryOptions(apiClient, makeCode!, modelCode!),
		enabled: !!makeCode && !!modelCode,
		retry: false,
		refetchOnWindowFocus: false,
		select,
	});
};

export const stockQueryOptions = (
	apiClient: ApiClientLike,
	makeCode: string,
	modelCode: string
) => ({
	queryKey: [MN_STOCK_QUERY_KEY, makeCode, modelCode],
	queryFn: () =>
		apiClient.motornetControllerService.getConfiguratorStock({
			makeCode,
			modelCode,
		}),
	staleTime: 5 * 60_000,
	gcTime: 10 * 60_000,
	retry: false,
});

/**
 * Veicoli in pronta consegna del modello selezionato.
 */
export const useStockList = (
	makeCode: string | null,
	modelCode: string | null
) => {
	const { apiClient } = useApiClient();

	return useQuery({
		...stockQueryOptions(apiClient, makeCode!, modelCode!),
		select: selectStockList,
		enabled: !!makeCode && !!modelCode,
		refetchOnWindowFocus: false,
	});
};

export const useVehicle = (ref: VehicleRef | null) => {
	const { apiClient } = useApiClient();

	return useQuery({
		...vehicleQueryOptions(apiClient, ref ?? ({} as VehicleRef)),
		enabled: !!ref?.eurotaxCode,
		retry: false,
		refetchOnWindowFocus: false,
	});
};
